import React, {
	useContext, useEffect, useRef, useState, useMemo, useCallback
} from 'react';
import classNames from 'classnames';
import uniqueId from 'uniqid';

import AddToCart from '../AddToCart';
import Button from '../Button';
import Link from '../Link';
import NumericInput from '../NumericInput';
import OOSModal from '../OOSModal';
import ProductOptions from '../ProductOptions';
import ProductSubscriptions from '../ProductSubscriptions';
import RenderStaticHTML from '../RenderStaticHTML';
import ShopPayModal from '../ShopPayModal';
import LoginModal from '../LoginModal';
import PractitionerStatusMessage from '../PractitionerStatusMessage';
import { MAX_PURCHASE_QUANTITY, MIN_PURCHASE_QUANTITY, FREE_SHIPPING_MSG } from '../../data/constants';
import { convertToElevarDataItem, formatPrice, getElevarListValue } from '../../utils';
import { getSellingPlanGroupsByProductId } from '../../utils/recharge';
import { ProductFormContext } from '../../context/product-form-context';
import { StoreContext } from '../../context/store-context';
import { AuthContext } from '../../context/auth-context';

const ns = `pdp-form`;

const PDPForm = ({
	location,
	shortDescription,
	setQuickShopOpen,
	shopPay,
	addToCartIcons,
  elevarUserEventLoaded,
}) => {
	const rootClassnames = classNames({
		[`${ ns }`]: true,
	});

	const {
		variant,
		isVariantAvailable,
		product,
		setShowErrors,
	} = useContext(ProductFormContext);

	const {
		client,
	} = React.useContext(StoreContext);

	const { state: authState } = useContext(AuthContext);
	const { auth = {}, userProperties, userPropertiesLoaded } = authState;

	const {
		shopifyId,
		priceRangeV2,
		title,
	} = product;

	const [quantity, setQuantity] = useState(1);
	const [sellingPlanGroup, setSellingPlanGroup] = useState(null);
	const [selectedSellingPlanId, setSelectedSellingPlanId] = useState(null);
	const [shopPayModalOpen, setShopPayModalOpen] = useState(false);
	const [loginModalOpen, setLoginModalOpen] = useState(false);
	const [newProductFlag, setNewProductFlag] = useState(null);

	const starsParentRef = useRef();

	const [isOOSModalOpen, setOOSModalOpen] = useState(false);
	const variantOOSMessage = product.tags.findIndex(e => { return e.includes('Exclude to be signed up'); }) < 0;
	const variantRef = useRef('');

	const tmpAvailabilityMap = {};
	product?.variants.forEach(_v => {
		tmpAvailabilityMap[_v.shopifyId || _v.id] = _v.availableForSale;
		return null;
	});

	const price = useMemo(() => {
    return variant ? formatPrice(priceRangeV2.minVariantPrice.currencyCode, variant.price) : null;
  }, [variant, priceRangeV2]);

	useEffect(() => {
		if (!starsParentRef.current) return;
		starsParentRef.current.addEventListener('mouseenter', e => { e.stopPropagation(); }, true);
	}, [starsParentRef]);

	useEffect(() => {
		if (product.tags.includes('New')) {
			setNewProductFlag(true);
		}
	}, [product]);

	useEffect(() => {
		client.product.fetch(product.storefrontId || product.id).then(_p => {
			const availMap = {};
			_p?.variants.forEach(_v => {
				availMap[_v.id] = _v.available;
				return null;
			}); //  Load object with properties as ${id}:${available}
		});
	}, [client.product, product.storefrontId, product.id]);

	useEffect(() => {
		if (!shopifyId) return;
		const productId = shopifyId?.split('/').pop();
		getSellingPlanGroupsByProductId(productId)
			.then(spg => {
				setSellingPlanGroup(spg);
			})
			.catch(err => {
				setSellingPlanGroup(undefined);
				// eslint-disable-next-line
				console.error('Error', err);
			});
	}, [shopifyId]);

	const elevarViewItemEvent = () => {
		window.ElevarDataLayer = window.ElevarDataLayer ?? [];
		window.ElevarDataLayer.push({
			event: 'dl_view_item',
			user_properties: userProperties,
			ecommerce: {
				currencyCode: 'USD',
				detail: {
					actionField: {
						list: getElevarListValue({ item: product, location }),
						action: 'detail',
					},
					products: [ convertToElevarDataItem({ item: product, location, variant }) ],
				},
			},
		});
	};

	useEffect(() => {
		if (elevarUserEventLoaded !== undefined) {
			if (variant && JSON.stringify(variant) !== variantRef.current && elevarUserEventLoaded) {
				elevarViewItemEvent();
				variantRef.current = JSON.stringify(variant);
			}
		} else if (variant && JSON.stringify(variant) !== variantRef.current && userPropertiesLoaded) {
			elevarViewItemEvent();
			variantRef.current = JSON.stringify(variant);
		}
	}, [variant, userPropertiesLoaded, elevarUserEventLoaded]);

	const msrpMetafield = variant?.metafields.find((metafield) => metafield.key === 'msrp') || product?.metafields.find((metafield) => metafield.key === 'msrp') || {};

	const msrpPrice = msrpMetafield.value ? formatPrice(
		priceRangeV2.minVariantPrice.currencyCode,
		msrpMetafield.value
	) : null;

	const handleQuantityChange = useCallback((newQuantity) => {
		setQuantity(Math.max(1, parseInt(newQuantity, 10) || 1));
	}, []);

	const incrementQuantity = useCallback(() => {
		setQuantity(q => Math.min(q + 1, MAX_PURCHASE_QUANTITY));
	}, []);

	const decrementQuantity = useCallback(() => {
		setQuantity(q => Math.max(MIN_PURCHASE_QUANTITY, q - 1));
	}, []);

	return (
		<div className={rootClassnames}>
			{newProductFlag && <div className="new-product-flag">NEW</div>}
			<h1 className={`${ ns }__title h3`}>{title}</h1>
			<div className={`${ ns }__stars`}>
				<div key={new Date().getTime()} ref={starsParentRef} data-bv-show={'rating_summary'} data-bv-product-id={shopifyId?.split('/').pop()} />
			</div>
			{auth.isAuthorized	&& (
				<div className={`${ ns }__price h4`}>
					<span>{price}</span>
				</div>
			)}
			{msrpPrice && (
				<div className={`${ ns }__msrp-price`}>
					<span>MSRP: {msrpPrice}</span>
				</div>
			)}
			{shortDescription && (
				<div className={`${ ns }__short-description`}>
					<RenderStaticHTML html={shortDescription.value} />
				</div>
			)}
			{!auth.isAuthenticated && (
				<Button
					className={`${ ns }__button`}
					onClick={() => { return setLoginModalOpen(true); }}
				>
					Log In To Purchase
				</Button>
			)}
			<LoginModal
				isLoginModalOpen={loginModalOpen}
				onRequestClose={() => { return setLoginModalOpen(false); }}
			/>
			{auth.isAuthenticated && !auth.isAuthorized && (
				<span className={`${ ns }__button`} style={{ color: '#e28f26' }}>
					<PractitionerStatusMessage />
				</span>
			)}
			{auth.isAuthorized && (
				<>
					<div className={`${ ns }__option-groups`}>
						<ProductOptions location={location} />
					</div>

					{isVariantAvailable() !== null && sellingPlanGroup && (
						<ProductSubscriptions
							quantity={quantity}
							price={variant?.price}
							priceRangeV2={priceRangeV2}
							sellingPlanGroup={sellingPlanGroup}
							updateSelectedSellingPlanId={setSelectedSellingPlanId}
						/>
					)}
					{isVariantAvailable() !== null && (
						variantOOSMessage && variant && !isVariantAvailable() ? (
							<div className={`${ ns }__out-of-stock`}>
								<p className={`${ ns }__out-of-stock__message`}>
									Currently out of stock. Recieve an email when
									item is back in stock by clicking below.
								</p>
								<div className={`${ ns }__quantity-cart`}>
									<button className={'klaviyo-bis-trigger add-to-cart'} onClick={() => { setOOSModalOpen(true); }}>
										Notify Me When Available
									</button>
								</div>
								<OOSModal
									variant={variant}
									isOOSModalOpen={isOOSModalOpen}
									onRequestClose={() => { setOOSModalOpen(false); }}
								/>
							</div>
						) : (
							<div className={`${ ns }__quantity-cart ${ !isVariantAvailable() ? 'disabled' : '' }`}>
								<NumericInput
									aria-label="Quantity"
									onIncrement={incrementQuantity}
									onDecrement={decrementQuantity}
									onChange={e => handleQuantityChange(e.currentTarget.value)}
									value={quantity}
									min={MIN_PURCHASE_QUANTITY}
									max={MAX_PURCHASE_QUANTITY}
								/>
								<AddToCart
									product={product}
									variant={variant}
									location={location}
									variantId={variant ? variant.storefrontId : null}
									quantity={quantity}
									available={isVariantAvailable()}
									// price={price}
									setQuickShopOpen={setQuickShopOpen}
									setShowErrors={setShowErrors}
									sellingPlanGroup={sellingPlanGroup}
									selectedSellingPlanId={selectedSellingPlanId}
									variants={product?.variants}
								/>
							</div>
						))}
					{shopPay?.text && (
						<div className={`${ ns }__shop-pay`}>
							<p>
								{shopPay?.text && (
								// eslint-disable-next-line react/jsx-no-useless-fragment
									<>
										{shopPay.text}
									</>
								)}
								{shopPay?.logo && (
									<RenderStaticHTML renderDiv={false} html={shopPay.logo} />
								)}
								{shopPay?.linkText && (
									<button onClick={() => { setShopPayModalOpen(true); }}>
										{shopPay?.linkText}
									</button>
								)}
							</p>

							<ShopPayModal
								shopPayModalOpen={shopPayModalOpen}
								setShopPayModalOpen={setShopPayModalOpen}
								content={shopPay?.modal}
							/>
						</div>
					)}
					<p className={`${ ns }__free-shipping`}>
						{ FREE_SHIPPING_MSG }
					</p>
					{addToCartIcons?.icons?.length > 0 && (
						<div className={`${ ns }__add-to-cart-icons`}>
							{addToCartIcons.icons.map(icon => {
								return (
									<div className={`${ ns }__add-to-cart-icon`} key={uniqueId('nn')}>
										<RenderStaticHTML html={icon.icon} />
										<p>{icon.label}</p>
									</div>
								);
							})}
						</div>
					)}
					<Link
						className={`${ ns }__product-link`}
						to={`/products/${ product.handle }`}
						aria-label={`View ${ title } product page`}
					>
						View Product Details
					</Link>
				</>
			)}
		</div>
	);
};

export default PDPForm;
